import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, BookOpen, Calendar, UserCheck, MessageCircle,
  Edit3, Trash2, ChevronDown, ChevronUp, User as UserIcon, Heart, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { DBS_TYPES, FAITH_STATUSES, fmtDate, dbsTypeLabel, stageLabel } from "../lib/constants";

interface Group {
  id: string; name: string; leader_id: string | null; leader_name?: string;
  meeting_rhythm: string | null; active: number; dbs_type: string | null; created_at: number;
}
interface Participant { id: string; name: string; faith_status: string; pathway_stage: string }
interface SessionRow {
  id: string; session_date: number; scripture: string | null;
  learned_about_god: string | null; what_will_i_do: string | null;
  who_will_i_share: string | null; prayer_points: string | null;
  next_date: number | null; attendance_count: number;
}
interface Leader { id: string; name: string }

export default function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [editing, setEditing] = useState(false);
  const [recordingSession, setRecordingSession] = useState(false);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  const load = async () => {
    const [g, l] = await Promise.all([
      api.get<{ group: Group; participants: Participant[]; sessions: SessionRow[] }>(`/groups/${id}`),
      api.get<{ leaders: Leader[] }>("/leaders"),
    ]);
    setGroup(g.group);
    setParticipants(g.participants);
    setSessions(g.sessions ?? []);
    setLeaders(l.leaders);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (!group) return <div className="text-ink-500">Loading…</div>;

  const dbs = DBS_TYPES.find((d) => d.id === group.dbs_type);
  const lastSession = sessions[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/groups" className="btn-ghost"><ArrowLeft className="h-4 w-4" /> Back to Groups</Link>
      </div>

      <div className="card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{group.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              {group.active ? (
                <span className="badge bg-green-100 text-green-800">Active</span>
              ) : (
                <span className="badge bg-ink-100 text-ink-500">Inactive</span>
              )}
              {dbs && <span className={`badge ${dbs.chip}`}>{dbs.short}</span>}
              <span className="badge bg-ink-100 text-ink-700">{participants.length} participant{participants.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setEditing(true)}>
              <Edit3 className="h-4 w-4" /> Edit
            </button>
            <button className="btn-primary" onClick={() => setRecordingSession(true)}>
              <Plus className="h-4 w-4" /> Record session
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
          <Field icon={<UserIcon className="h-4 w-4" />} label="Leader" value={group.leader_name ?? "—"} />
          <Field icon={<Calendar className="h-4 w-4" />} label="Meeting rhythm" value={group.meeting_rhythm ?? "—"} />
          <Field icon={<BookOpen className="h-4 w-4" />} label="Last session"
            value={lastSession ? fmtDate(lastSession.session_date) : "Not yet recorded"} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">DBS Sessions ({sessions.length})</h2>
          </div>
          {sessions.length === 0 ? (
            <div className="card p-8 text-center text-sm text-ink-500">
              No sessions recorded yet. Click <strong>Record session</strong> after your next DBS to capture what God said.
            </div>
          ) : (
            sessions.map((s) => {
              const open = openSessionId === s.id;
              return (
                <div key={s.id} className="card p-5">
                  <button
                    className="w-full text-left"
                    onClick={() => setOpenSessionId(open ? null : s.id)}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold">{fmtDate(s.session_date)}</div>
                        <div className="text-xs text-ink-500 mt-0.5">
                          {s.scripture ? <>📖 {s.scripture} · </> : null}
                          {s.attendance_count} {s.attendance_count === 1 ? "attendee" : "attendees"}
                        </div>
                      </div>
                      <span className="text-ink-400">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
                    </div>
                  </button>
                  {open && (
                    <SessionDetail
                      groupId={group.id}
                      sessionId={s.id}
                      onChanged={load}
                    />
                  )}
                </div>
              );
            })
          )}
        </section>

        <aside className="space-y-4">
          <div className="card p-6">
            <h3 className="font-semibold mb-3">Participants ({participants.length})</h3>
            {participants.length === 0 ? (
              <div className="text-sm text-ink-500">No participants yet. Add people on the People page and assign them to this group.</div>
            ) : (
              <ul className="space-y-2">
                {participants.map((p) => {
                  const f = FAITH_STATUSES.find((x) => x.id === p.faith_status);
                  return (
                    <li key={p.id}>
                      <Link to={`/people/${p.id}`} className="flex items-center justify-between text-sm hover:text-brand">
                        <span className="flex items-center gap-2">
                          {f && <span className={`h-1.5 w-1.5 rounded-full ${f.dot}`} />}
                          {p.name}
                        </span>
                        <span className="text-xs text-ink-400">{stageLabel(p.pathway_stage)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {dbs && (
            <div className={`card p-5 ${dbs.chip}`}>
              <div className="font-semibold flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${dbs.dot}`} />{dbs.short}
              </div>
              <div className="text-xs mt-1 opacity-80">{dbs.label}</div>
              <div className="text-xs mt-2 opacity-70">{dbs.blurb}</div>
            </div>
          )}
        </aside>
      </div>

      <Modal open={editing} onClose={() => setEditing(false)} title="Edit group">
        <GroupForm leaders={leaders} initial={group}
          onSaved={() => { setEditing(false); load(); }}
          onCancel={() => setEditing(false)}
          onDelete={async () => {
            if (!confirm("Delete this group? Sessions will be deleted too.")) return;
            await api.del(`/groups/${group.id}`);
            nav("/groups");
          }} />
      </Modal>

      <Modal open={recordingSession} onClose={() => setRecordingSession(false)} title="Record DBS session" wide>
        <SessionForm
          groupId={group.id}
          participants={participants}
          onSaved={() => { setRecordingSession(false); load(); }}
          onCancel={() => setRecordingSession(false)}
        />
      </Modal>
    </div>
  );
}

function Field({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-500 uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
      <div className="text-ink-900 mt-0.5">{value}</div>
    </div>
  );
}

// ----- Session create form -----
function SessionForm({
  groupId, participants, onSaved, onCancel,
}: {
  groupId: string;
  participants: Participant[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const todayInput = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    session_date: todayInput,
    scripture: "",
    learned_about_god: "",
    what_will_i_do: "",
    who_will_i_share: "",
    prayer_points: "",
    next_date: "",
  });
  const [attendeeIds, setAttendeeIds] = useState<Set<string>>(new Set(participants.map((p) => p.id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (id: string) => {
    setAttendeeIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const epoch = (s: string) => s ? Math.floor(new Date(s + "T00:00:00").getTime() / 1000) : null;
      await api.post(`/groups/${groupId}/sessions`, {
        session_date: epoch(form.session_date),
        scripture: form.scripture || null,
        learned_about_god: form.learned_about_god || null,
        what_will_i_do: form.what_will_i_do || null,
        who_will_i_share: form.who_will_i_share || null,
        prayer_points: form.prayer_points || null,
        next_date: epoch(form.next_date),
        attendee_ids: [...attendeeIds],
      });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4 text-sm">
      <div className="grid sm:grid-cols-2 gap-3">
        <Labeled label="Session date">
          <input className="input" type="date" required value={form.session_date} onChange={(e) => set("session_date", e.target.value)} />
        </Labeled>
        <Labeled label="Scripture passage">
          <input className="input" placeholder="e.g. Luke 5:1-11" value={form.scripture} onChange={(e) => set("scripture", e.target.value)} />
        </Labeled>
      </div>

      <Labeled label="What did we learn about God?" icon={<BookOpen className="h-3.5 w-3.5" />}>
        <textarea className="input min-h-[70px]" value={form.learned_about_god} onChange={(e) => set("learned_about_god", e.target.value)} />
      </Labeled>
      <Labeled label="What will I do this week?" icon={<Sparkles className="h-3.5 w-3.5" />}>
        <textarea className="input min-h-[60px]" value={form.what_will_i_do} onChange={(e) => set("what_will_i_do", e.target.value)} />
      </Labeled>
      <Labeled label="Who will I share this with?" icon={<MessageCircle className="h-3.5 w-3.5" />}>
        <textarea className="input min-h-[60px]" value={form.who_will_i_share} onChange={(e) => set("who_will_i_share", e.target.value)} />
      </Labeled>
      <Labeled label="Prayer points" icon={<Heart className="h-3.5 w-3.5" />}>
        <textarea className="input min-h-[60px]" value={form.prayer_points} onChange={(e) => set("prayer_points", e.target.value)} />
      </Labeled>

      <Labeled label="Attendance" icon={<UserCheck className="h-3.5 w-3.5" />}>
        {participants.length === 0 ? (
          <div className="text-xs text-ink-500">No participants in this group yet — add people on the People page first.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-1.5">
            {participants.map((p) => {
              const checked = attendeeIds.has(p.id);
              return (
                <label key={p.id} className={`flex items-center gap-2 rounded-md p-2 ring-1 cursor-pointer transition ${
                  checked ? "ring-brand bg-brand-50/40" : "ring-ink-200 hover:bg-ink-50"
                }`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                  <span>{p.name}</span>
                </label>
              );
            })}
          </div>
        )}
      </Labeled>

      <Labeled label="Planned next session (optional)">
        <input className="input" type="date" value={form.next_date} onChange={(e) => set("next_date", e.target.value)} />
      </Labeled>

      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save session"}</button>
      </div>
    </form>
  );
}

function Labeled({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="label flex items-center gap-1.5">{icon}{label}</label>
      {children}
    </div>
  );
}

// ----- Expanded session detail (inline within the list) -----
function SessionDetail({
  groupId, sessionId, onChanged,
}: { groupId: string; sessionId: string; onChanged: () => void }) {
  const [data, setData] = useState<{
    session: SessionRow & { recorded_by_name?: string | null };
    attendees: { id: string; name: string }[];
  } | null>(null);
  useEffect(() => {
    api.get<{ session: any; attendees: any[] }>(`/groups/${groupId}/sessions/${sessionId}`)
      .then((r) => setData(r as any));
  }, [sessionId, groupId]);

  if (!data) return <div className="text-xs text-ink-400 mt-3">Loading…</div>;
  const s = data.session;

  return (
    <div className="mt-4 pt-4 border-t border-ink-100 space-y-3 text-sm">
      {s.learned_about_god && <Reading title="What did we learn about God?" icon={<BookOpen className="h-3.5 w-3.5" />} body={s.learned_about_god} />}
      {s.what_will_i_do && <Reading title="What will I do this week?" icon={<Sparkles className="h-3.5 w-3.5" />} body={s.what_will_i_do} />}
      {s.who_will_i_share && <Reading title="Who will I share this with?" icon={<MessageCircle className="h-3.5 w-3.5" />} body={s.who_will_i_share} />}
      {s.prayer_points && <Reading title="Prayer points" icon={<Heart className="h-3.5 w-3.5" />} body={s.prayer_points} />}

      {data.attendees.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 flex items-center gap-1.5">
            <UserCheck className="h-3.5 w-3.5" /> Attendance ({data.attendees.length})
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {data.attendees.map((a) => (
              <Link key={a.id} to={`/people/${a.id}`} className="badge bg-ink-100 text-ink-700 hover:bg-brand-50 hover:text-brand">{a.name}</Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 text-xs text-ink-400">
        <span>{s.recorded_by_name ? `Recorded by ${s.recorded_by_name}` : ""}{s.next_date ? ` · Next: ${fmtDate(s.next_date)}` : ""}</span>
        <button
          onClick={async () => {
            if (!confirm("Delete this session?")) return;
            await api.del(`/groups/${groupId}/sessions/${sessionId}`);
            onChanged();
          }}
          className="text-brand hover:underline inline-flex items-center gap-1"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </div>
  );
}

function Reading({ title, icon, body }: { title: string; icon: React.ReactNode; body: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 flex items-center gap-1.5">{icon}{title}</div>
      <div className="text-ink-700 mt-1 whitespace-pre-wrap">{body}</div>
    </div>
  );
}

// ----- Reuses the GroupForm shape from the existing Groups page -----
function GroupForm({
  leaders, initial, onSaved, onCancel, onDelete,
}: {
  leaders: Leader[]; initial?: Partial<Group>;
  onSaved: () => void; onCancel: () => void; onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    leader_id: initial?.leader_id ?? "",
    meeting_rhythm: initial?.meeting_rhythm ?? "",
    active: initial?.active ?? 1,
    dbs_type: initial?.dbs_type ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      setBusy(true); setErr(null);
      try {
        const payload = { ...form, leader_id: form.leader_id || null, dbs_type: form.dbs_type || null };
        if (initial && (initial as Group).id) {
          await api.patch(`/groups/${(initial as Group).id}`, payload);
        } else {
          await api.post("/groups", payload);
        }
        onSaved();
      } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
    }} className="space-y-3">
      <div>
        <label className="label">Group name</label>
        <input className="input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div>
        <label className="label">Leader</label>
        <select className="input" value={form.leader_id ?? ""} onChange={(e) => setForm((f) => ({ ...f, leader_id: e.target.value }))}>
          <option value="">— Unassigned —</option>
          {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Meeting rhythm</label>
        <input className="input" placeholder="e.g. Weekly — Thursdays 7:00 PM"
          value={form.meeting_rhythm ?? ""} onChange={(e) => setForm((f) => ({ ...f, meeting_rhythm: e.target.value }))} />
      </div>
      <div>
        <label className="label">DBS type</label>
        <select className="input" value={form.dbs_type ?? ""} onChange={(e) => setForm((f) => ({ ...f, dbs_type: e.target.value }))}>
          <option value="">— Not set —</option>
          {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))} />
        Active
      </label>
      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-between pt-2">
        <div>{onDelete && <button type="button" onClick={onDelete} className="text-brand text-sm">Delete</button>}</div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </form>
  );
}
